import { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import moment from "moment";
import {
    listDynSupportByLocation,
    getDynSupportDownloadUrl,
    type DynSupportLocationFile,
} from "src/tecnica/modules/consolidate_files/tam/tamViewerService";
import { tableStyles } from "src/shared/util/tableStyles";

export const TamFilesTab = ({ locationId }: { locationId?: string }) => {
    const [rows, setRows] = useState<DynSupportLocationFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!locationId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await listDynSupportByLocation(locationId, "TAM");
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error al cargar archivos TAM");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [locationId]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleDownload = async (fileId: string) => {
        setDownloadingId(fileId);
        try {
            const { signedUrl } = await getDynSupportDownloadUrl(fileId);
            window.open(signedUrl, "_blank", "noopener,noreferrer");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error al descargar el archivo");
        } finally {
            setDownloadingId(null);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "executedAt",
            headerName: "Fecha del estudio",
            flex: 0.4,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Typography sx={{ py: "5px" }}>
                    {params.value ? moment(params.value).format("DD-MM-YYYY") : ""}
                </Typography>
            ),
        },
        {
            field: "fileName",
            headerName: "Archivo",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Tooltip title={params.row.fileName}>
                    <Typography
                        sx={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: "1.4",
                            py: "5px",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            display: "-webkit-box",
                        }}
                    >
                        {params.row.fileName}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: "wellNames",
            headerName: "Pozos",
            width: 90,
            headerAlign: "center",
            align: "center",
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const wells: string[] = params.row.wellNames ?? [];
                if (!wells.length) return <span>—</span>;
                return (
                    <Tooltip
                        title={
                            <Box sx={{ maxHeight: 240, overflow: "auto", py: 0.5 }}>
                                {wells.map((name, i) => (
                                    <Typography key={`${name}-${i}`} variant="caption" display="block">
                                        {name}
                                    </Typography>
                                ))}
                            </Box>
                        }
                    >
                        <IconButton size="small" color="default">
                            <FormatListBulletedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            width: 90,
            headerAlign: "center",
            align: "center",
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title="Descargar">
                    <span>
                        <IconButton
                            color="primary"
                            disabled={downloadingId === params.row.id}
                            onClick={() => handleDownload(params.row.id)}
                        >
                            {downloadingId === params.row.id ? (
                                <CircularProgress size={20} />
                            ) : (
                                <DownloadIcon />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography variant="h6" color="primary">
                    Archivos TAM asociados al pozo
                </Typography>
                <Tooltip title="Recargar">
                    <span>
                        <IconButton color="primary" onClick={() => void load()} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 3 }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption">Cargando archivos TAM…</Typography>
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{ p: 3 }}>
                    <Typography color="text.secondary">
                        No hay archivos TAM asociados a este pozo.
                    </Typography>
                </Box>
            ) : (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pageSizeOptions={[10, 20, 50, 100]}
                    getRowHeight={() => "auto"}
                    sx={{ ...tableStyles }}
                    disableRowSelectionOnClick
                />
            )}
        </Box>
    );
};

export default TamFilesTab;
